import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubmitButtonContent = ({ isSubmitting, isSubmitted }) => {
  if (isSubmitting) return <span className="flex items-center justify-center gap-2"><span className="spinner"></span>Sending...</span>;
  if (isSubmitted) return <span className="flex items-center justify-center gap-2"><CheckCircle size={20} />Message Sent!</span>;
  return <span className="flex items-center justify-center gap-2"><Send size={20} />Send Message</span>;
};

export default function ContactSection() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success("Message sent successfully!", { description: "We'll get back to you as soon as possible." });
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      }
    } catch {
      toast.error("Failed to send message", { description: "Please try again or contact us directly via email." });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setIsSubmitted(false), 3000);
    }
  };

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  return (
    <section id="contact" data-testid="contact-section" className="py-24 px-4 bg-[#111111]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="section-title text-4xl sm:text-5xl text-white mb-4">Get In Touch</h2>
          <p className="text-[#8A8A8A] mt-8 max-w-2xl mx-auto">Ready to enhance your vehicle's performance? Contact us today</p>
        </div>
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl text-white mb-6">Contact Information</h3>
              <div className="space-y-6">
                <a href="mailto:ttw.engineering26@gmail.com" data-testid="contact-email" className="flex items-center gap-4 text-[#B0B0B0] hover:text-[#FF3B30] transition-colors">
                  <div className="w-12 h-12 rounded-lg bg-[#1A1A1A] flex items-center justify-center border border-[#2A2A2A]"><Mail size={20} className="text-[#FF3B30]" /></div>
                  <div><p className="text-sm text-[#8A8A8A]">Email</p><p className="font-medium">ttw.engineering26@gmail.com</p></div>
                </a>
                <a href="tel:+31638617073" data-testid="contact-phone" className="flex items-center gap-4 text-[#B0B0B0] hover:text-[#FF3B30] transition-colors">
                  <div className="w-12 h-12 rounded-lg bg-[#1A1A1A] flex items-center justify-center border border-[#2A2A2A]"><Phone size={20} className="text-[#FF3B30]" /></div>
                  <div><p className="text-sm text-[#8A8A8A]">Phone</p><p className="font-medium">+31 6 38617073</p></div>
                </a>
              </div>
            </div>
            <div className="p-6 bg-[#1A1A1A] rounded-lg border border-[#2A2A2A]">
              <h4 className="text-white font-semibold mb-3">Why Choose TTW Engineering?</h4>
              <ul className="space-y-3 text-[#8A8A8A]">
                {["Expert knowledge of performance upgrades", "Professional diagnostics & tuning equipment", "Custom solutions for your specific needs"].map(t => (
                  <li key={t} className="flex items-start gap-3"><CheckCircle size={18} className="text-[#FF3B30] mt-0.5 flex-shrink-0" /><span>{t}</span></li>
                ))}
              </ul>
            </div>
          </div>
          <form onSubmit={handleSubmit} data-testid="contact-form" className="glass-card rounded-lg p-8">
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm text-[#8A8A8A] mb-2">Your Name *</label>
                  <Input id="name" name="name" data-testid="input-name" value={formData.name} onChange={handleChange} required className="form-input bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-[#4A4A4A]" placeholder="John Doe" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm text-[#8A8A8A] mb-2">Email Address *</label>
                  <Input id="email" name="email" type="email" data-testid="input-email" value={formData.email} onChange={handleChange} required className="form-input bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-[#4A4A4A]" placeholder="john@example.com" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm text-[#8A8A8A] mb-2">Phone Number</label>
                  <Input id="phone" name="phone" data-testid="input-phone" value={formData.phone} onChange={handleChange} className="form-input bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-[#4A4A4A]" placeholder="+31 6 12345678" />
                </div>
                <div>
                  <label htmlFor="service" className="block text-sm text-[#8A8A8A] mb-2">Service Interested In</label>
                  <Select value={formData.service} onValueChange={(value) => setFormData(prev => ({ ...prev, service: value }))}>
                    <SelectTrigger data-testid="select-service" className="form-input bg-[#1A1A1A] border-[#2A2A2A] text-white"><SelectValue placeholder="Select a service" /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                      <SelectItem value="chiptuning">Chiptuning & ECU Remapping</SelectItem>
                      <SelectItem value="racebuilds">Special Requests</SelectItem>
                      <SelectItem value="diagnostics">Advanced Diagnostics</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm text-[#8A8A8A] mb-2">Your Message *</label>
                <Textarea id="message" name="message" data-testid="input-message" value={formData.message} onChange={handleChange} required rows={5} className="form-input bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-[#4A4A4A] resize-none" placeholder="Tell us about your project or what you need help with..." />
              </div>
              <Button type="submit" data-testid="submit-button" disabled={isSubmitting} className="w-full btn-primary py-6 text-base font-semibold uppercase tracking-wider">
                <SubmitButtonContent isSubmitting={isSubmitting} isSubmitted={isSubmitted} />
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
